import React from 'react';
import './SupportPage.css';

const CookiesPolicy = () => {
  return (
    <div className="support-page">
      <div className="support-header">
        <h1 className="support-title">Cookie Policy</h1>
        <p className="support-subtitle">
          Learn about how we use cookies and similar technologies to improve your experience on V Football.
        </p>
      </div>
      
      <div className="support-content">
        <div className="info-box">
          <p>
            <strong>Last Updated:</strong> January 2025
          </p>
        </div>
        
        <h2>1. What Are Cookies?</h2>
        <p>
          Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. 
          They are widely used to make websites work more efficiently and to provide information to website owners about how users interact with their sites.
        </p>
        
        <h2>2. How We Use Cookies</h2>
        <p>V Football uses cookies and similar technologies for several purposes:</p>
        
        <h3>Essential Cookies</h3>
        <p>These cookies are necessary for our website to function properly and cannot be disabled:</p>
        <ul>
          <li>Authentication and security</li>
          <li>Remembering your preferences and settings</li>
          <li>Enabling core website functionality</li>
          <li>Load balancing and performance optimization</li>
        </ul>
        
        <h3>Analytics Cookies</h3>
        <p>These cookies help us understand how visitors use our website:</p>
        <ul>
          <li>Tracking page views and user journeys</li>
          <li>Measuring website performance</li>
          <li>Understanding which content is most popular</li>
          <li>Identifying technical issues and errors</li>
        </ul>
        
        <h3>Functional Cookies</h3>
        <p>These cookies enhance your experience by remembering your choices:</p>
        <ul>
          <li>Language preferences</li>
          <li>Favorite teams and leagues</li>
          <li>Display preferences (dark/light mode)</li>
          <li>Recently viewed matches and news</li>
        </ul>
        
        <h3>Advertising Cookies</h3>
        <p>These cookies are used to deliver relevant advertisements:</p>
        <ul>
          <li>Displaying targeted ads based on your interests</li>
          <li>Measuring advertising effectiveness</li>
          <li>Preventing the same ad from being shown repeatedly</li>
          <li>Providing anonymous reporting to advertisers</li>
        </ul>
        
        <h2>3. Types of Cookies We Use</h2>
        
        <h3>Session Cookies</h3>
        <p>
          These temporary cookies are deleted when you close your browser. They help maintain your session 
          as you navigate through our website and ensure security.
        </p>
        
        <h3>Persistent Cookies</h3>
        <p>
          These cookies remain on your device for a set period or until you delete them. They remember 
          your preferences and settings for future visits.
        </p>
        
        <h3>First-Party Cookies</h3>
        <p>
          These cookies are set directly by V Football and are used to provide core functionality 
          and improve your user experience.
        </p>
        
        <h3>Third-Party Cookies</h3>
        <p>
          These cookies are set by external services we use, such as analytics providers, 
          advertising networks, and social media platforms.
        </p>
        
        <h2>4. Third-Party Services</h2>
        <p>We may use third-party services that place cookies on your device:</p>
        
        <h3>Google Analytics</h3>
        <ul>
          <li>Tracks website usage and performance</li>
          <li>Provides insights into user behavior</li>
          <li>Helps us improve our content and services</li>
        </ul>
        
        <h3>Social Media Platforms</h3>
        <ul>
          <li>Enables social sharing functionality</li>
          <li>Allows integration with social media accounts</li>
          <li>Provides social media advertising capabilities</li>
        </ul>
        
        <h3>Advertising Networks</h3>
        <ul>
          <li>Delivers targeted advertisements</li>
          <li>Measures ad performance and engagement</li>
          <li>Provides revenue to support our free service</li>
        </ul>
        
        <h2>5. Managing Your Cookie Preferences</h2>
        
        <h3>Browser Settings</h3>
        <p>You can control cookies through your browser settings:</p>
        <ul>
          <li><strong>Accept all cookies:</strong> Allow all cookies to be stored</li>
          <li><strong>Block all cookies:</strong> Prevent all cookies from being stored</li>
          <li><strong>Block third-party cookies:</strong> Only allow first-party cookies</li>
          <li><strong>Ask before storing:</strong> Get prompted for each cookie</li>
        </ul>
        
        <h3>Browser-Specific Instructions</h3>
        <ul>
          <li><strong>Chrome:</strong> Settings > Privacy and Security > Cookies and other site data</li>
          <li><strong>Firefox:</strong> Options > Privacy & Security > Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences > Privacy > Cookies and website data</li>
          <li><strong>Edge:</strong> Settings > Cookies and site permissions > Cookies and site data</li>
        </ul>
        
        <h3>Our Cookie Preference Center</h3>
        <p>
          When you first visit our website, you'll see a cookie banner allowing you to customize your preferences. 
          You can also update these preferences at any time by clicking the "Cookie Settings" link in our footer.
        </p>
        
        <h2>6. Impact of Disabling Cookies</h2>
        <p>Disabling cookies may affect your experience on our website:</p>
        
        <h3>Essential Cookies</h3>
        <ul>
          <li>Website may not function properly</li>
          <li>You may not be able to log in or access personalized features</li>
          <li>Settings and preferences will not be saved</li>
        </ul>
        
        <h3>Analytics Cookies</h3>
        <ul>
          <li>We cannot improve our website based on usage data</li>
          <li>Performance issues may not be identified</li>
          <li>Content optimization will be limited</li>
        </ul>
        
        <h3>Functional Cookies</h3>
        <ul>
          <li>Personalization features will not work</li>
          <li>You'll need to reset preferences on each visit</li>
          <li>Favorite teams and leagues will not be remembered</li>
        </ul>
        
        <h3>Advertising Cookies</h3>
        <ul>
          <li>Ads may be less relevant to your interests</li>
          <li>You may see repeated advertisements</li>
          <li>Ad revenue supporting our free service may decrease</li>
        </ul>
        
        <h2>7. Other Tracking Technologies</h2>
        <p>In addition to cookies, we may use other tracking technologies:</p>
        
        <h3>Web Beacons</h3>
        <p>
          Small transparent images used to track user behavior and measure the effectiveness 
          of email campaigns and advertisements.
        </p>
        
        <h3>Local Storage</h3>
        <p>
          Browser storage that allows us to store larger amounts of data locally 
          to improve website performance and functionality.
        </p>
        
        <h3>Session Storage</h3>
        <p>
          Temporary storage that maintains data for the duration of your browser session 
          to ensure smooth navigation and functionality.
        </p>
        
        <h2>8. Data Retention</h2>
        <p>Different types of cookies have different retention periods:</p>
        <ul>
          <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
          <li><strong>Functional cookies:</strong> Typically stored for 30 days to 2 years</li>
          <li><strong>Analytics cookies:</strong> Usually stored for 13-26 months</li>
          <li><strong>Advertising cookies:</strong> Generally stored for 30 days to 1 year</li>
        </ul>
        
        <h2>9. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in our practices 
          or applicable laws. We will notify you of any material changes by posting the updated 
          policy on our website and updating the "Last Updated" date.
        </p>
        
        <h2>10. Contact Us</h2>
        <p>
          If you have any questions about our use of cookies or this Cookie Policy, please contact us:
        </p>
        <ul>
          <li>Email: <a href="mailto:privacy@vfootball.com">privacy@vfootball.com</a></li>
          <li>Contact Form: <a href="/contact">Contact Us</a></li>
        </ul>
        
        <p>
          For more information about our data practices, please also review our <a href="/privacy">Privacy Policy</a> 
          and <a href="/terms">Terms of Service</a>.
        </p>
        
        <div className="info-box">
          <p>
            <strong>Your Choice Matters:</strong> We respect your privacy choices. You can customize your cookie 
            preferences at any time to control how we collect and use information about your visits to our website.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;